import React from 'react';
import './Legal.css';

const Terms = () => {
  return (
    <div className="legal-page">
      <section className="legal-hero">
        <div className="container">
          <h1>Terms of Use</h1>
          <p className="legal-effective">Effective Date: January 1, 2025</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="legal-content">
            <div className="legal-intro">
              <p>
                Welcome to Pathway Labs. These Terms of Use ("Terms") govern your access to and
                use of the website, products, and services provided by Pathway Labs, Inc.
                ("Pathway Labs," "we," "us," or "our"). By accessing or using our services,
                you agree to be bound by these Terms.
              </p>
            </div>

            <div className="legal-section">
              <h2>1. Acceptance of Terms</h2>
              <p>
                By accessing our website or using our services, you acknowledge that you have
                read, understood, and agree to be bound by these Terms and our Privacy Policy.
                If you do not agree to these Terms, you may not access or use our services.
              </p>
              <p>
                If you are using our services on behalf of an organization, you represent and
                warrant that you have the authority to bind that organization to these Terms.
              </p>
            </div>

            <div className="legal-section">
              <h2>2. Description of Services</h2>
              <p>
                Pathway Labs provides software infrastructure, workflow applications, data
                integration tools, and related services designed for mission-driven organizations.
                Our services include but are not limited to:
              </p>
              <ul>
                <li>Workflow and program management applications</li>
                <li>Data integration and reporting tools</li>
                <li>Compliance-oriented software features</li>
                <li>Training and education system infrastructure</li>
                <li>AI-assisted tools and governance features</li>
              </ul>
              <p>
                We reserve the right to modify, suspend, or discontinue any aspect of our
                services at any time with reasonable notice.
              </p>
            </div>

            <div className="legal-section">
              <h2>3. User Responsibilities</h2>
              <p>When using our services, you agree to:</p>
              <ul>
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Use our services only for lawful purposes</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Respect the intellectual property rights of Pathway Labs and others</li>
                <li>Not attempt to gain unauthorized access to our systems</li>
                <li>Not interfere with or disrupt our services or servers</li>
                <li>Not use our services to transmit harmful code or malware</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>4. Data Ownership and Responsibility</h2>
              <p>
                <strong>Your Data Remains Yours.</strong> You retain all ownership rights to
                the data you input into our systems. Pathway Labs does not claim ownership
                of your data.
              </p>
              <p>
                <strong>Your Responsibility.</strong> You are solely responsible for:
              </p>
              <ul>
                <li>The accuracy and legality of data you input into our systems</li>
                <li>Ensuring you have the right to use and share such data</li>
                <li>Configuring appropriate access controls within your organization</li>
                <li>Complying with applicable data protection laws and regulations</li>
                <li>Backing up your data as appropriate for your needs</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>5. Intellectual Property</h2>
              <p>
                All content, features, and functionality of our services—including but not
                limited to software, text, graphics, logos, and documentation—are owned by
                Pathway Labs or its licensors and are protected by intellectual property laws.
              </p>
              <p>
                Subject to these Terms, we grant you a limited, non-exclusive, non-transferable
                license to access and use our services for your internal business purposes.
                This license does not include the right to:
              </p>
              <ul>
                <li>Modify, copy, or create derivative works of our services</li>
                <li>Reverse engineer, decompile, or disassemble our software</li>
                <li>Remove or alter any proprietary notices or labels</li>
                <li>Sublicense, sell, or redistribute our services</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>6. No Professional Advice</h2>
              <p>
                <strong>Pathway Labs does not provide legal, financial, tax, or compliance advice.</strong>
              </p>
              <p>
                Our services are tools designed to assist your organization's operations. They are
                not substitutes for professional counsel. Any compliance-oriented features,
                workflow automation, or reporting tools are intended as aids and do not ensure
                adherence to any specific law, regulation, or standard (including but not limited
                to HIPAA, FERPA, COPPA, or OMB 2 CFR 200).
              </p>
              <p>
                You should consult qualified professionals for legal, financial, tax, and
                regulatory matters.
              </p>
            </div>

            <div className="legal-section">
              <h2>7. AI-Assisted Features</h2>
              <p>
                Some of our services may include AI-assisted features. You acknowledge and agree that:
              </p>
              <ul>
                <li>AI outputs are suggestions only and may contain errors</li>
                <li>AI outputs should not be relied upon as factual, legal, or clinical determinations</li>
                <li>You are responsible for reviewing and validating all AI-generated information</li>
                <li>Human oversight is required for all decisions based on AI outputs</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>8. Service Availability</h2>
              <p>
                We strive to maintain high availability of our services but do not guarantee
                uninterrupted access. Our services may be temporarily unavailable due to:
              </p>
              <ul>
                <li>Scheduled maintenance (with advance notice when possible)</li>
                <li>Emergency repairs or security updates</li>
                <li>Factors beyond our reasonable control</li>
              </ul>
              <p>
                We are not liable for any damages resulting from service interruptions.
              </p>
            </div>

            <div className="legal-section">
              <h2>9. Limitation of Liability</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, PATHWAY LABS AND ITS OFFICERS, DIRECTORS,
                EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS,
                DATA, USE, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF OUR SERVICES.
              </p>
              <p>
                OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING UNDER THESE TERMS SHALL NOT EXCEED
                THE AMOUNTS PAID BY YOU TO PATHWAY LABS IN THE TWELVE (12) MONTHS PRECEDING
                THE CLAIM.
              </p>
            </div>

            <div className="legal-section">
              <h2>10. Disclaimer of Warranties</h2>
              <p>
                OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY
                KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY,
                FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
              <p>
                WE DO NOT WARRANT THAT OUR SERVICES WILL BE ERROR-FREE, SECURE, OR UNINTERRUPTED,
                OR THAT DEFECTS WILL BE CORRECTED.
              </p>
            </div>

            <div className="legal-section">
              <h2>11. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Pathway Labs and its officers,
                directors, employees, and agents from any claims, damages, losses, liabilities,
                and expenses (including reasonable attorneys' fees) arising out of or related to:
              </p>
              <ul>
                <li>Your use of our services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights</li>
                <li>Any data you submit through our services</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>12. Termination</h2>
              <p>
                We may suspend or terminate your access to our services at any time for any reason,
                including if we reasonably believe you have violated these Terms. Upon termination:
              </p>
              <ul>
                <li>Your right to use our services will immediately cease</li>
                <li>We may delete your account and associated data after a reasonable period</li>
                <li>Provisions that by their nature should survive will remain in effect</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>13. Governing Law and Disputes</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of
                the State of Delaware, without regard to its conflict of law provisions.
              </p>
              <p>
                Any disputes arising from these Terms or your use of our services shall be
                resolved through good-faith negotiation. If negotiation fails, disputes shall
                be submitted to binding arbitration in accordance with the rules of the
                American Arbitration Association.
              </p>
            </div>

            <div className="legal-section">
              <h2>14. Changes to These Terms</h2>
              <p>
                We may update these Terms from time to time. We will notify you of material
                changes by posting the updated Terms on our website with a new effective date.
                Your continued use of our services after such changes constitutes acceptance
                of the updated Terms.
              </p>
            </div>

            <div className="legal-section">
              <h2>15. General Provisions</h2>
              <ul>
                <li>
                  <strong>Entire Agreement:</strong> These Terms constitute the entire agreement
                  between you and Pathway Labs regarding the subject matter hereof.
                </li>
                <li>
                  <strong>Severability:</strong> If any provision of these Terms is found invalid
                  or unenforceable, the remaining provisions will continue in effect.
                </li>
                <li>
                  <strong>Waiver:</strong> Our failure to enforce any right or provision of these
                  Terms shall not be considered a waiver of such right or provision.
                </li>
                <li>
                  <strong>Assignment:</strong> You may not assign these Terms without our prior
                  written consent. We may assign our rights and obligations without restriction.
                </li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>16. Contact Us</h2>
              <p>
                If you have questions about these Terms, please contact us:
              </p>
              <div className="contact-block">
                <p><strong>Pathway Labs, Inc.</strong></p>
                <p>Email: legal@pathwaylabs.io</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
